import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useStreamingDimension } from '../hooks/useStreamingDimension';
import { buildDimensionRows } from '../dimensionRows';
import { streamingDimensionPath, streamingSectionPath } from '../streamingPaths';
import { streamingBlockMessages as messages } from '../streamingBlockMessages';
import { StreamingBlock } from '../parts/StreamingBlock';
import { RankedRow } from '../parts/RankedRow';
import { DimensionName } from '../parts/DimensionName';
import { StreamingPlanLockCard } from '../parts/StreamingPlanLockCard';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { streamingEarningsMessages } from '../streamingEarningsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The rows of a block on the overview (design b1: the top 5 and a link to the section). */
const BLOCK_ROWS = 5;

interface DimensionBlockProps {
  accountId: number;
  dimension: 'store' | 'country';
  days: number;
  /** A track of the distributor: the block shows the stores or countries of that track (design d2). */
  trackExternalId?: string;
  /** A title in place of "Stores" / "Countries" ("By store" on a track). */
  title?: string;
  onNavigate: (path: string) => void;
  /** The block sits inside another block (the track detail): no shadow. */
  nested?: boolean;
}

/** The top stores or countries of the period, with the share and the change (design b1, d2). */
const DimensionBlockComponent: React.FC<DimensionBlockProps> = ({
  accountId,
  dimension,
  days,
  trackExternalId,
  title,
  onNavigate,
  nested = false,
}) => {
  const intl = useIntl();
  const { data, isLoading, error, retry } = useStreamingDimension({
    accountId,
    dimension,
    days,
    trackExternalId,
    skip: false,
  });
  const rows = useMemo(() => buildDimensionRows(data?.totals ?? [], 'streams'), [data]);
  const top = useMemo(() => rows.slice(0, BLOCK_ROWS), [rows]);
  const isStore = dimension === 'store';
  // The plan does not split the streams (Free): the block shows the plan card and opens nothing.
  const splitHidden = data?.splitHidden === true;

  return (
    <StreamingBlock
      title={title ?? intl.formatMessage(isStore ? messages.stores : messages.countries)}
      moreLabel={
        splitHidden
          ? undefined
          : intl.formatMessage(isStore ? messages.allStores : messages.allCountries, { count: rows.length })
      }
      onMore={splitHidden ? undefined : () => onNavigate(streamingSectionPath(isStore ? 'stores' : 'countries', days))}
      isLoading={isLoading}
      error={error}
      retry={retry}
      testId={`streaming-block-${dimension}`}
      nested={nested}
    >
      {splitHidden ? (
        <StreamingPlanLockCard
          feature={SMLUpgradeFeature.streamingSplit}
          title={streamingEarningsMessages.splitPlanTitle}
          testId={`streaming-block-${dimension}-plan-lock`}
        />
      ) : top.length === 0 ? (
        <p className={styles.faint}>{intl.formatMessage(messages.emptyList)}</p>
      ) : (
        <ol className={styles.rows}>
          {top.map((row) => (
            <RankedRow
              key={row.value}
              rank={row.rank}
              name={<DimensionName dimension={dimension} value={row.value} />}
              share={row.share}
              value={row.number}
              changePct={row.changePct}
              onOpen={() => onNavigate(streamingDimensionPath(dimension, row.value, days))}
            />
          ))}
        </ol>
      )}
      {isStore && trackExternalId === undefined && top.length > 0 ? (
        <p className={styles.footnote} data-testid="streaming-stores-sum-note">
          {intl.formatMessage(messages.storesSumNote)}
        </p>
      ) : null}
    </StreamingBlock>
  );
};

export const DimensionBlock = TypedMemo(DimensionBlockComponent);
